import React from 'react';
import { Building2, Wrench, Paintbrush, Shield, Droplets, Headphones } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { services, agricoltura } from '../utils/mockData';

const Servizi = () => {
  const iconMap = {
    Building2: Building2,
    Wrench: Wrench,
    Paintbrush: Paintbrush,
    Shield: Shield,
    Droplets: Droplets,
    Headphones: Headphones
  };

  const serviceImages = [
    "https://images.pexels.com/photos/36003985/pexels-photo-36003985.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    "https://images.pexels.com/photos/4312841/pexels-photo-4312841.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    "https://images.pexels.com/photos/4981782/pexels-photo-4981782.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwyfHxjb25zdHJ1Y3Rpb24lMjBzaXRlfGVufDB8fHx8MTc3NDU0MjY3Mnww&ixlib=rb-4.1.0&q=85"
            alt="Construction Services"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-slate-800/70"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              I Nostri Servizi
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Soluzioni complete per professionisti dell'edilizia e dell'agricoltura
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
              Cosa Offriamo
            </h2>
            <p className="text-xl text-slate-600">
              Servizi professionali per supportare ogni fase del tuo progetto
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <Card key={service.id} className="group hover:shadow-2xl transition-all duration-300 border-none">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform">
                      <Icon size={32} />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-800 mb-4 group-hover:text-orange-600 transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Agricultural Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-6">
                  {agricoltura.title}
                </h2>
                <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                  {agricoltura.description}
                </p>

                <div className="space-y-6">
                  {agricoltura.products.map((product) => (
                    <Card key={product.id} className="border-none shadow-md hover:shadow-lg transition-shadow">
                      <CardContent className="p-6">
                        <h3 className="text-xl font-bold text-slate-800 mb-2">
                          {product.name}
                        </h3>
                        <p className="text-slate-600">
                          {product.description}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div className="rounded-2xl overflow-hidden shadow-xl">
                  <img 
                    src="https://images.pexels.com/photos/8775928/pexels-photo-8775928.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                    alt="Innesto di pesca - Prodotti Agricoli Professionali"
                    className="w-full h-auto object-cover"
                  />
                </div>
                <div className="bg-gradient-to-br from-slate-800 to-slate-700 rounded-xl p-6 text-white">
                  <h4 className="text-xl font-bold mb-3">Offerta Speciale Innesti</h4>
                  <p className="text-slate-200 mb-3">
                    Inizia la stagione degli innesti con <span className="text-orange-400 font-bold">ICOBIT</span>
                  </p>
                  <ul className="space-y-2 text-sm text-slate-300">
                    <li className="flex items-start gap-2">
                      <span className="text-orange-400">✓</span>
                      <span>Per ogni latta da 15 kg: nastro dielettrico nero e pennello lungo 40 cm in omaggio</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-orange-400">✓</span>
                      <span>Prodotti professionali di alta qualità</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-12 text-center">
              Perché Sceglierci
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              {serviceImages.map((img, index) => (
                <div key={index} className="rounded-xl overflow-hidden shadow-lg">
                  <img 
                    src={img}
                    alt={`Service ${index + 1}`}
                    className="w-full h-64 object-cover hover:scale-110 transition-transform duration-500"
                  />
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-br from-slate-800 to-slate-700 rounded-2xl p-12 text-white">
              <h3 className="text-3xl font-bold mb-6">I Vantaggi di Lavorare con Noi</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-xl font-semibold text-orange-400 mb-2">Esperienza Consolidata</h4>
                  <p className="text-slate-200">Anni di attività al servizio del territorio</p>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-orange-400 mb-2">Prodotti Certificati</h4>
                  <p className="text-slate-200">Solo materiali di prima qualità</p>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-orange-400 mb-2">Consegna Rapida</h4>
                  <p className="text-slate-200">Servizio di consegna veloce e puntuale</p>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-orange-400 mb-2">Supporto Tecnico</h4>
                  <p className="text-slate-200">Consulenza specializzata gratuita</p>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-orange-400 mb-2">Prezzi Competitivi</h4>
                  <p className="text-slate-200">Migliore rapporto qualità-prezzo</p>
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-orange-400 mb-2">Disponibilità</h4>
                  <p className="text-slate-200">Ampio stock sempre disponibile</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">
              Hai Bisogno dei Nostri Servizi?
            </h2>
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              Contattaci per discutere delle tue esigenze specifiche e ricevere un preventivo personalizzato.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/contatti" 
                className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 text-white font-semibold rounded-lg shadow-lg transition-all"
              >
                Contattaci Ora
              </a>
              <a 
                href={`https://wa.me/393482988266`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-green-600 text-green-600 hover:bg-green-600 hover:text-white font-semibold rounded-lg transition-all"
              >
                WhatsApp: 348 2988266
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Servizi;
